import {
  Drawer,
  Descriptions,
  Table,
  Space,
  Typography,
  Image,
  Tooltip,
  Button,
  Modal,
  message,
  Tag,
  DatePicker,
  Card,
  Empty,
  Tabs,
} from 'antd';
import {
  EditOutlined,
  ReloadOutlined,
  SettingOutlined,
  ExclamationCircleOutlined,
  ThunderboltOutlined,
  StopOutlined,
  BarChartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  ToolOutlined,
  LockOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import {
  FaTrashAlt,
  FaQrcode,
  FaLink,
  FaPlug,
  FaBolt,
  FaCircle,
} from 'react-icons/fa';
import { AiOutlineClockCircle, AiOutlineThunderbolt } from 'react-icons/ai';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import QRCodeModal from '../../../components/QRCodeModal';
import ConnectModal from '../../../components/ConnectModal';
import OcppCommandDrawer from './OcppCommandDrawer';
import CreateCharger from './CreateCharger';
import TransactionDetailsModal from '../../../components/Modals/TransactionDetailsModal';
import {
  useDeleteCharger,
  useStopChargingModal,
  useGetSingleCharger,
  useGetChargerUtilization,
  useGetChargerUptime,
  useBlockCharger,
} from '../Hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useOperatorStore } from '../../../store/useOperatorStore';
import {
  useIsEditAllowedWithOperator,
  parseLogs,
} from '../../../utils/utils';
import { getErrorMessage } from '../../../utils/errorHandler';
import { GiSettingsKnobs } from 'react-icons/gi';
import { CSS2, Default, GBT, Type2 } from '../../../components/Icons';
import dayjs from 'dayjs';
import { Area, Column } from '@ant-design/plots';
import LocationSingleDrawer from './LocationSingleDrawer';
import PropTypes from 'prop-types';
import { LuLogs } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { BsFilePdf, BsListColumnsReverse } from 'react-icons/bs';
import { FaFileImage } from 'react-icons/fa6';
import { useOCPPLogs } from '../../../Hooks/Index';
import OCPPLogsTable from '../../../components/ui/OCPPLogsTable';
import {
  buildOcppLogsSearch,
  resolveOcppLogsOperatorId,
} from '../../../utils/ocppLogsNavigation';
import { Grid } from 'antd';

const { Title } = Typography;

const { useBreakpoint } = Grid;

// 🔷 Status Mapping Function
const getStatusTag = (status, t) => {
  const statusColors = {
    available: {
      color: 'green',
      label: t('status.available'),
      icon: <FaPlug />,
    },
    preparing: {
      color: 'gold',
      label: t('status.preparing'),
      icon: <SettingOutlined spin />,
    },
    charging: {
      color: 'orange',
      label: t('status.charging'),
      icon: <AiOutlineThunderbolt className="size-3" />,
    },
    configuring: {
      color: 'gold',
      label: t('status.configuring'),
      icon: <SettingOutlined spin />,
    },
    suspendedEVSE: {
      color: 'red',
      label: t('status.suspendedEVSE', 'Suspended EVSE'),
      icon: <ExclamationCircleOutlined />,
    },
    unavailable: {
      color: 'gray',
      label: t('status.unavailable', 'Unavailable'),
      icon: <FaCircle />,
    },
    faulted: {
      color: 'red',
      label: t('status.faulty'),
      icon: <FaBolt />,
    },
    finishing: {
      color: 'gold',
      label: t('status.finishing', 'Finishing'),
      icon: <AiOutlineClockCircle />,
    },
    reserved: {
      color: 'blue',
      label: t('status.reserved'),
      icon: <AiOutlineClockCircle />,
    },
  };

  return (
    statusColors[status?.toLowerCase()] || {
      color: 'default',
      label: t(status, { defaultValue: status }),
      icon: <FaCircle />,
    }
  );
};

const SingleChargerDrawer = ({
  isOpen,
  onClose,
  selectedCharger,
  passoperatorId,
}) => {
  const [isQRModalOpen, setQRModalOpen] = useState(false);
  const [isConnectModalOpen, setConnectModalOpen] = useState(false);
  const [chargerKey, setChargerKey] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [isOcppDrawerOpen, setOcppDrawerOpen] = useState(false);
  const [openCharger, setOpenCharger] = useState(false);
  const [selectedChargerId, setSelectedChargerId] = useState(null);
  const [isTransactionModalOpen, setTransactionModalOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [stopChargingModalOpen, setStopChargingModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'days'),
    dayjs(),
  ]);
  const [showUtilization, setShowUtilization] = useState(false);
  const [showUptime, setShowUptime] = useState(false);
  const [utilizationData, setUtilizationData] = useState(null);
  const [uptimeData, setUptimeData] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('1');
  const [nestedActiveTab, setNestedActiveTab] = useState('1');
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockAction, setBlockAction] = useState('BLOCK');
  const {
    data,
    isLoading: isOCPPLogsLoading,
    error,
    isError,
    refetch: refechOcppLogs,
  } = useOCPPLogs(selectedCharger, 300, {
    enabled: !!selectedCharger && activeTab == 2,
  });
  const isEditAllowed = useIsEditAllowedWithOperator();
  const { operatorId: opID } = useOperatorStore();
  const operatorId = opID || passoperatorId;
  const queryClient = useQueryClient();
  const { t } = useTranslation(['chargerPage', 'location']);
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const {
    data: chargerData,
    isLoading,
    error: chargerError,
    isError: isChargerError,
    refetch,
  } = useGetSingleCharger({ operatorId, id: selectedCharger });


  const { mutate: deleteCharger, isPending: isDeleteChargerLoading } =
    useDeleteCharger({ operatorId });
  const { mutate: stopCharging, isPending: isStopChargingLoading } =
    useStopChargingModal();

  const { mutate: fetchUtilization, isPending: isUtilizationLoading } =
    useGetChargerUtilization();

  const { mutate: fetchUptime, isPending: isUptimeLoading, } =
    useGetChargerUptime();

  const { mutate: blockCharger, isLoading: isBlockLoading } = useBlockCharger({
    onSuccess: () => {
      message.success(
        blockAction === 'BLOCK'
          ? t('blockSuccessMessage')
          : t('unblockSuccessMessage')
      );
      queryClient.invalidateQueries(['GET_Charger_location']);
      refetch();
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.message || error?.response?.data?.status_message
      );
    },
  });

  // Add useEffect to update images when chargerData changes
  useEffect(() => {
    if (chargerData?.images) {
      setImages(chargerData.images);
      setSelectedImageIndex(0); // Reset to first image when images change
    }
  }, [chargerData?.images]);

  // Memoize parsed logs
  const logs = useMemo(() => parseLogs(data), [data]);

  const handleShowQR = (serialNumber) => {
    const customerUrl = import.meta.env.VITE_CHARGER_CUSTOMER_URL;
    if (customerUrl && serialNumber) {
      setQRModalOpen(true);
    }
  };

  const confirmStopCharging = () => {
    stopCharging(
      {
        operatorId,
        serialNumber: chargerData.serialNumber,
        ocppTransactionId: stopChargingModalOpen.ocppTransactionId,
      },
      {
        onSuccess: () => {
          message.success(t('messages.transactionStopped'));
          setTimeout(() => {
            message.info(t('messages.updating'));
            refetch();
          }, 3000);
          setStopChargingModalOpen(false);
        },
        onError: (error) => {
          message.error(getErrorMessage(error, t('messages.transactionStopError')));
        },
      }
    );
  };

  const handleDeleteCharger = (chargerId, opId) => {
    Modal.confirm({
      title: t('modal.confirmDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('modal.confirmDeleteMessage'),
      okText: t('modal.yesDelete'),
      okButtonProps: {
        loading: isDeleteChargerLoading,
      },
      okType: 'danger',
      cancelText: t('buttons.cancel'),
      onOk: () => {
        deleteCharger({ id: chargerId, opId: opId }, {
          onSuccess: () => {
            message.success(t('messages.chargerDeleted'));
            queryClient.invalidateQueries(['GET_Charger_location']);
            refetch();
            onClose();
          },
          onError: (error) => {
            message.error(
              error?.response?.data?.message || t('messages.deleteFailed')
            );
          },
        });
      },
    });
  };

  const connectorsWithTransactions = chargerData?.connectors?.map(
    (connector) => {
      const activeTransaction = chargerData?.activeChargeHistory?.find(
        (tx) => tx.connectorId === connector._id
      );
      return {
        ...connector,
        activeTransaction,
      };
    }
  );



  const chargerDetailsItems = [
    {
      key: 'stationLocation',
      label: t('location.name'),
      children: (
        <Button
          type="link"
          onClick={() => {
            setSelectedLocation(chargerData?.locationData[0]);
          }}
          className="!p-0 !h-auto"
        >
          {chargerData?.locationData[0]?.locationName ||
            t('common.notAvailable')}
        </Button>
      ),
      span: { xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 },
    },
    {
      key: 'serial',
      label: t('charger.serialNumber'),
      children: chargerData?.serialNumber || t('common.notAvailable'),
    },
    {
      key: 'power',
      label: t('charger.power'),
      children: (
        <div className="!whitespace-nowrap">
          {chargerData?.maxCapacity != null ? `${chargerData.maxCapacity} kW` : t('common.notAvailable')}
        </div>
      ),
    },
    {
      key: 'type',
      label: t('charger.chargerType'),
      children: (
        <div className="!whitespace-nowrap">
          {chargerData?.powerType || t('common.unknown')}
        </div>
      ),
    },
    {
      key: 'status',
      label: t('charger.status'),
      children: (
        <div className="!whitespace-nowrap">
          <Tag
            variant="outlined"
            color={
              chargerData?.status === 'ONLINE'
                ? 'green'
                : chargerData?.status === 'OFFLINE'
                  ? 'red'
                  : chargerData?.status === 'FAULTED'
                    ? 'orange'
                    : chargerData?.status === 'MAINTANCE'
                      ? 'blue'
                      : 'default'
            }
          >
            {chargerData?.status === 'ONLINE' && <CheckCircleOutlined />}
            {chargerData?.status === 'OFFLINE' && <CloseCircleOutlined />}
            {chargerData?.status === 'FAULTED' && <WarningOutlined />}
            {chargerData?.status === 'MAINTANCE' && <ToolOutlined />}{' '}
            {chargerData?.status ? t(chargerData.status, { defaultValue: chargerData.status }) : t('common.unknown')}
          </Tag>
          {chargerData?.isBlocked && (
            <Tag color="red" variant="outlined">{t('charger.blocked')}</Tag>
          )}
        </div>
      ),
    },
    {
      key: 'geo',
      label: t('charger.geolocation'),
      children: (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2 items-start">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <span className="font-medium">{t('location.latitude')}:</span>
                <span className="font-mono">
                  {chargerData?.location?.coordinates?.[1]?.toFixed(6) || '—'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{t('location.longitude')}:</span>
                <span className="font-mono">
                  {chargerData?.location?.coordinates?.[0]?.toFixed(6) || '—'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={`https://www.google.com/maps?q=${chargerData?.location?.coordinates?.[1]},${chargerData?.location?.coordinates?.[0]}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
              >
                {t('location.viewOnMap')}
              </a>
            </div>
          </div>
        </div>
      ),
      span: { xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 },
    },
    {
      key: 'firmwareVersion',
      label: t('charger.firmwareVersion'),
      className: 'text-primary',
      children: chargerData?.firmwareVersion || t('common.notAvailable'),
    },

    {
      key: 'offlineAt',
      label: t('charger.lastOffline'),
      children: chargerData?.lastOffline
        ? dayjs(chargerData?.lastOfflineAt).format('DD-MM-YYYY HH:mm')
        : t('common.notAvailable'),
    },
    {
      key: 'accessType',
      label: t('charger.accessType'),
      children: chargerData?.isPrivate ? (
        <Tag color="magenta" variant="outlined">{t('charger.private')}</Tag>
      ) : (
        <Tag color="green" variant="outlined">{t('charger.public')}</Tag>
      ),
    },
  ];

  const connectorColumns = [
    {
      title: t('connector.id'),
      dataIndex: 'connectorId',
      key: 'connectorId',
      align: 'center',
      width: isMobile ? 60 : 80,
    },
    {
      title: t('connector.type'),
      dataIndex: 'connectorType',
      key: 'connectorType',
      align: 'center',
      width: isMobile ? 80 : 100,
      render: (connectorType, record) => {
        // Map connectorType to corresponding image
        const connectorImages = {
          TYPE2: <Type2 className={isMobile ? "size-4" : "size-6"} status={record.status} />,
          CCS2: <CSS2 className={isMobile ? "size-4" : "size-6"} status={record.status} />,
          GBT: <GBT className={isMobile ? "size-4" : "size-6"} status={record.status} />,
        };

        // Fallback to Default if connectorType is not in the list
        const connectorImage = connectorImages[connectorType] || (
          <Default className={isMobile ? "size-4" : "size-6"} status={record.status} />
        );

        return (
          <div className="justify-center flex items-center gap-1 sm:gap-2">
            <Tooltip title={connectorType || t('common.unknown')}>
              {connectorImage}
            </Tooltip>
          </div>
        );
      },
    },

    {
      title: t('connector.status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: isMobile ? 100 : 120,
      render: (text) => {
        if (text?.toLowerCase() === 'ready') return null;
        const status = getStatusTag(text, t);
        return (
          <div className="justify-center flex items-center gap-1">
            <Tag
              variant="outlined"
              icon={status.icon}
              className="!flex !items-center gap-1 !max-w-fit !ml-0"
              color={status.color}
            >
              {status.label}
            </Tag>
          </div>
        );
      },
    },
    {
      title: t('connector.errorCode'),
      dataIndex: 'errorCode',
      key: 'errorCode',
      align: 'center',
      width: isMobile ? 80 : 100,
      render: (text) => {
        if (!text || text === 'NoError') return text === 'NoError' ? t('NoError') : '—';
        return t(text, { defaultValue: text });
      },
    },
    {
      title: t('connector.info'),
      dataIndex: 'info',
      key: 'info',
      align: 'center',
      width: isMobile ? 80 : 100,
      render: (text) => {
        if (!text || text === 'NoError') return text === 'NoError' ? t('NoError') : '—';
        return t(text, { defaultValue: text });
      },
    },
    {
      title: t('buttons.actions'),
      key: 'actions',
      align: 'center',
      width: isMobile ? 100 : 120,
      render: (_, record) => {
        const isActive = !!record.activeTransaction;
        return (
          <Space size={isMobile ? 'small' : 'middle'}>
            {isActive && (
              <Tooltip title={t('buttons.viewTransaction')}>
                <Button
                  icon={<ThunderboltOutlined />}
                  onClick={() => {
                    setSelectedTransactionId(record.activeTransaction._id);
                    setTransactionModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
            {isActive && isEditAllowed && (
              <Tooltip title={t('buttons.stopCharging')}>
                <Button
                  danger
                  icon={<StopOutlined />}
                  loading={isStopChargingLoading}
                  onClick={() =>
                    setStopChargingModalOpen(record.activeTransaction)
                  }
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  const handleFetchUtilization = () => {
    if (dateRange[0] && dateRange[1]) {
      fetchUtilization(
        {
          id: selectedCharger,
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD'),
        },
        {
          onSuccess: (data) => {
            if (data?.data && Array.isArray(data?.data)) {
              const convertedData = data.data.map((item) => ({
                ...item,
                consumption: Number((item.consumption / 1000).toFixed(4)),
              }));
              setUtilizationData(convertedData);
            } else {
              setUtilizationData([]);
              message.warning(t('utilization.noData'));
            }
          },
          onError: (error) => {
            message.error(getErrorMessage(error, t('utilization.fetchError')));
            console.error(error);
            setUtilizationData([]);
          },
        }
      );
    }
  };

  const columns = [
    {
      title: t('ocppTimestamp'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: isMobile ? 120 : 180,
      align: 'center',
      className: 'whitespace-nowrap',
      sorter: (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
    },
    {
      title: t('ocppMessageDetails'),
      dataIndex: 'messageDetails',
      key: 'messageDetails',
      render: (text, record) => (
        <pre
          className={`whitespace-pre-wrap ${isMobile ? 'text-xs' : 'text-sm'} p-2 rounded overflow-auto max-h-40 transition-all ${record.isNew ? 'bg-yellow-200 animate-pulse' : 'bg-gray-100'
            }`}
        >
          {text}
        </pre>
      ),
    },
  ];

  const handleFetchUptime = () => {
    if (dateRange[0] && dateRange[1]) {
      fetchUptime(
        {
          id: selectedCharger,
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD'),
        },
        {
          onSuccess: (data) => {
            if (data?.data && Array.isArray(data?.data)) {
              const processedData = data?.data.map((item) => ({
                date: item.date,
                uptime: Number(item.uptimePercentage),
              }));
              setUptimeData(processedData);
            } else {
              setUptimeData([]);
              message.warning(t('uptime.noData'));
            }
          },
          onError: (error) => {
            message.error(getErrorMessage(error, t('uptime.fetchError')));
            console.error(error);
            setUptimeData([]);
          },
        }
      );
    }
  };

  // Update the useEffect to handle both graphs
  useEffect(() => {
    if (activeTab === '1' && showUtilization && dateRange[0] && dateRange[1]) {
      handleFetchUtilization();
    } else if (
      activeTab === '2' &&
      showUptime &&
      dateRange[0] &&
      dateRange[1]
    ) {
      handleFetchUptime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, activeTab, showUtilization, showUptime]);

  // Force graph resize when switching tabs
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === '1' && utilizationGraphRef.current) {
        window.dispatchEvent(new Event('resize'));
      } else if (activeTab === '2' && uptimeGraphRef.current) {
        window.dispatchEvent(new Event('resize'));
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [activeTab, showUtilization, showUptime, utilizationData, uptimeData]);

  const config = {
    data: utilizationData || [],
    xField: 'date',
    yField: 'consumption',
    autoFit: true,
    style: {
      fill: '#067C60',
    },
    meta: {
      consumption: {
        alias: t('utilization.consumption'),
        formatter: (v) => `${v} ${t('utilization.unit')}`,
      },
      date: {
        formatter: (v) => {
          const viewType = utilizationData?.[0]?.viewType || 'daily';
          switch (viewType) {
            case 'yearly':
              return dayjs(v, 'DD-MM-YYYY').format('YYYY');
            case 'monthly':
              return dayjs(v, 'DD-MM-YYYY').format('MMM YYYY');
            case 'daily':
            default:
              return dayjs(v, 'DD-MM-YYYY').format('MMM DD');
          }
        },
      },
    },
    axis: {
      y: {
        labelFormatter: (val) => `${val} ${t('utilization.unit')}`,
      },
    },
    inset: 10,
  };

  const uptimeConfig = {
    data: uptimeData || [],
    xField: 'date',
    yField: 'uptime',
    autoFit: true,
    axis: {
      y: { labelFormatter: (val) => `${val}%` },
    },
    areaStyle: {
      fill: 'l(270) 0:#67e8f9 1:#0ea5e9', // CSS linear gradient
      fillOpacity: 0.7,
    },
    tooltip: {
      title: t('uptime.uptime'),
      items: [{ channel: 'y', valueFormatter: (val) => `${val}%` }],
    },
    color: '#0ee945', // Line and area color
    meta: {
      uptime: {
        alias: t('uptime.uptime'),
        formatter: (v) => `${v.toFixed(2)}%`,
      },
      date: {
        formatter: (v) => {
          const viewType = uptimeData?.[0]?.viewType || 'daily';
          switch (viewType) {
            case 'yearly':
              return dayjs(v, 'YYYY-MM-DD').format('YYYY');
            case 'monthly':
              return dayjs(v, 'YYYY-MM-DD').format('MMM YYYY');
            case 'daily':
            default:
              return dayjs(v, 'YYYY-MM-DD').format('MMM DD');
          }
        },
      },
    },
    yAxis: {
      min: 0,
      max: 100,
      label: {
        formatter: (val) => `${val}%`,
      },
    },
    line: {
      style: {
        stroke: '#1ce026',
        strokeWidth: 3,
      },
    },
    style: {
      fill: '#baf7d5',
    },
    inset: 10,
  };

  const utilizationGraphRef = useRef(null);
  const uptimeGraphRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  // Export handlers
  const handleExportImage = async (ref, filename) => {
    if (!ref.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(ref.current, { useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = imgData;
      link.download = filename + '.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async (ref, filename) => {
    if (!ref.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(ref.current, { useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4',
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      // Fit image to page
      const imgProps = canvas;
      const ratio = Math.min(
        pageWidth / imgProps.width,
        pageHeight / imgProps.height
      );
      const imgWidth = imgProps.width * ratio;
      const imgHeight = imgProps.height * ratio;
      pdf.addImage(
        imgData,
        'PNG',
        (pageWidth - imgWidth) / 2,
        40,
        imgWidth,
        imgHeight
      );
      pdf.save(filename + '.pdf');
    } finally {
      setExporting(false);
    }
  };

  const items = [
    {
      key: '1',
      label: t('utilization.title'),
      children: (
        <Space orientation="vertical" size="middle" className="w-full">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <DatePicker.RangePicker
              value={dateRange}
              onChange={(dates) => {
                setDateRange(dates);
                setUtilizationData(null);
                setShowUtilization(false);
              }}
              allowClear={false}
              presets={[
                { label: tCommon('today'), value: [dayjs(), dayjs()] },
                {
                  label: tCommon('yesterday'),
                  value: [dayjs().add(-1, 'd'), dayjs().add(-1, 'd')],
                },
                {
                  label: tCommon('last7Days'),
                  value: [dayjs().add(-7, 'd'), dayjs()],
                },
                {
                  label: tCommon('last30Days'),
                  value: [dayjs().add(-30, 'd'), dayjs()],
                },
                {
                  label: tCommon('thisMonth'),
                  value: [dayjs().startOf('month'), dayjs().endOf('month')],
                },
                {
                  label: tCommon('lastMonth'),
                  value: [
                    dayjs().add(-1, 'month').startOf('month'),
                    dayjs().add(-1, 'month').endOf('month'),
                  ],
                },
              ]}
            />
            <Button
              type=""
              icon={<BarChartOutlined />}
              onClick={() => {
                setShowUtilization(!showUtilization);
                if (!showUtilization) {
                  handleFetchUtilization();
                }
              }}
            >
              {showUtilization
                ? t('utilization.hideGraph')
                : t('utilization.showGraph')}
            </Button>
          </div>
          {showUtilization && (
            <Card>
              <div className="flex flex-col sm:flex-row justify-between gap-2 mb-2">
                <div>
                  <Title level={isMobile ? 5 : 5} className="!mb-0">
                    {t('utilization.title')}
                  </Title>
                </div>
                <div className="flex gap-2">
                  <Tooltip title={tCommon('downloadAsImage')}>
                    <Button
                      loading={exporting}
                      icon={<FaFileImage />}
                      onClick={() =>
                        handleExportImage(
                          utilizationGraphRef,
                          'UtilizationGraph'
                        )
                      }
                    />
                  </Tooltip>
                  <Tooltip title={tCommon('downloadAsPdf')}>
                    <Button
                      loading={exporting}
                      icon={<BsFilePdf />}
                      onClick={() =>
                        handleExportPDF(utilizationGraphRef, 'UtilizationGraph')
                      }
                    />
                  </Tooltip>
                </div>
              </div>
              <div className={`${isMobile ? 'h-[250px]' : 'h-[300px]'} min-h-[250px] w-full`} ref={utilizationGraphRef}>
                {utilizationData &&
                  utilizationData?.length > 0 &&
                  !isUtilizationLoading ? (
                  <Column {...config} />
                ) : (
                  <div className="flex justify-center items-center h-full">
                    <Empty description={t('utilization.noData')} />
                  </div>
                )}
              </div>
            </Card>
          )}
        </Space>
      ),
    },
    {
      key: '2',
      label: t('uptime.title'),
      children: (
        <Space orientation="vertical" size="middle" className="w-full">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <DatePicker.RangePicker
              value={dateRange}
              onChange={(dates) => {
                setDateRange(dates);
                setUptimeData(null);
                setShowUptime(false);
              }}
              allowClear={false}
              disabledDate={(current) =>
                current && current > dayjs().endOf('day')
              }
              presets={[
                { label: tCommon('today'), value: [dayjs(), dayjs()] },
                {
                  label: tCommon('yesterday'),
                  value: [dayjs().add(-1, 'd'), dayjs().add(-1, 'd')],
                },
                {
                  label: tCommon('last7Days'),
                  value: [dayjs().add(-7, 'd'), dayjs()],
                },
                {
                  label: tCommon('last30Days'),
                  value: [dayjs().add(-30, 'd'), dayjs()],
                },
                {
                  label: tCommon('thisMonth'),
                  value: [dayjs().startOf('month'), dayjs().endOf('month')],
                },
                {
                  label: tCommon('lastMonth'),
                  value: [
                    dayjs().add(-1, 'month').startOf('month'),
                    dayjs().add(-1, 'month').endOf('month'),
                  ],
                },
              ]}
            />
            <Button
              type=""
              icon={<BarChartOutlined />}
              onClick={() => {
                setShowUptime(!showUptime);
                if (!showUptime) {
                  handleFetchUptime();
                }
              }}
            >
              {showUptime ? t('uptime.hideGraph') : t('uptime.showGraph')}
            </Button>
          </div>
          <Card>
            <div className="flex flex-col sm:flex-row justify-between gap-2 mb-2">
              <div>
                <Title level={isMobile ? 5 : 5} className="!mb-0">
                  {t('uptime.title')}
                </Title>
              </div>
              <div className="flex gap-2">
                <Tooltip title={tCommon('downloadAsImage')}>
                  <Button
                    loading={exporting}
                    icon={<FaFileImage />}
                    onClick={() =>
                      handleExportImage(uptimeGraphRef, 'UptimeGraph')
                    }
                  />
                </Tooltip>
                <Tooltip title={tCommon('downloadAsPdf')}>
                  <Button
                    loading={exporting}
                    icon={<BsFilePdf />}
                    onClick={() =>
                      handleExportPDF(uptimeGraphRef, 'UptimeGraph')
                    }
                  />
                </Tooltip>
              </div>
            </div>
            <div className={`${isMobile ? 'h-[250px]' : 'h-[300px]'} min-h-[250px] w-full`} ref={uptimeGraphRef}>
              {uptimeData && uptimeData?.length > 0 && !isUptimeLoading ? (
                <Area {...uptimeConfig} />
              ) : (
                <div className="flex justify-center items-center h-full">
                  <Empty description={t('uptime.noData')} />
                </div>
              )}
            </div>
          </Card>
        </Space>
      ),
    },
  ];

  const showBlockConfirm = () => {
    setBlockAction(chargerData?.isBlocked ? 'UNBLOCK' : 'BLOCK');
    setBlockModalOpen(true);
  };

  const confirmBlockCharger = () => {
    const isBlocked = blockAction === 'BLOCK';
    blockCharger({
      id: chargerData._id,
      status: isBlocked ? 'BLOCKED' : 'ACTIVE',
    });
    setBlockModalOpen(false);
  };
  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      width={isMobile ? '100%' : '60%'}
      loading={isLoading}
      footer={
        <div className="flex justify-center gap-2 sm:gap-4 items-center flex-wrap">
          <div>
            <Tooltip title={t('buttons.showQRCode')}>
              <Button
                shape="circle"
                icon={<FaQrcode />}
                onClick={() => handleShowQR(chargerData._id)}
              />
            </Tooltip>
          </div>
          <div>
            <Tooltip title={t('buttons.connectURL')}>
              <Button
                shape="circle"
                icon={<FaLink />}
                onClick={() => {
                  setChargerKey(chargerData?.chargerKey);
                  setSerialNumber(chargerData?.serialNumber);
                  setConnectModalOpen(true);
                }}
              />
            </Tooltip>
          </div>
          {isEditAllowed && (
            <div className="flex justify-center gap-2 sm:gap-4 items-center flex-wrap">
              <Tooltip title={t('buttons.edit')}>
                <Button
                  shape="circle"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setOpenCharger(true);
                    setSelectedChargerId(chargerData._id);
                  }}
                />
              </Tooltip>
              <Tooltip title={t('buttons.delete')}>
                <Button
                  shape="circle"
                  danger
                  icon={<FaTrashAlt />}
                  disabled={!['READY', 'OFFLINE'].includes(chargerData?.status)}
                  loading={isDeleteChargerLoading}
                  onClick={() => handleDeleteCharger(chargerData._id, chargerData?.operatorId)}
                />
              </Tooltip>
              <Tooltip title={t('buttons.allOcppControls')}>
                <Button
                  shape="circle"
                  disabled={
                    !(
                      chargerData?.status === 'ONLINE' ||
                      chargerData?.status === 'FAULTED'
                    )
                  }
                  icon={<GiSettingsKnobs />}
                  onClick={() => setOcppDrawerOpen(true)}
                />
              </Tooltip>
              <Tooltip
                title={
                  chargerData?.isBlocked
                    ? t('unblock', 'Unblock')
                    : t('block', 'Block')
                }
              >
                <Button
                  type="default"
                  icon={
                    chargerData?.isBlocked ? (
                      <UnlockOutlined />
                    ) : (
                      <LockOutlined />
                    )
                  }
                  loading={isBlockLoading}
                  onClick={showBlockConfirm}
                  disabled={isDeleteChargerLoading}
                />
              </Tooltip>
            </div>
          )}
        </div>
      }
      title={
        <div className="flex justify-between items-center flex-row gap-2 sm:gap-0">
          <div className="flex items-center gap-2 w-full">
            <Title level={isMobile ? 5 : 4} className="!mb-0 text-center sm:text-left flex items-center gap-2">
              {chargerData?.name}
            </Title>
          </div>
          <div className='flex flex-row gap-2'>
            {isEditAllowed && (
              <Tooltip title={t('viewOcppLogs')}>
                <Button
                  type="default"
                  icon={<LuLogs />}
                  onClick={() => {
                    const ocppOperatorId = resolveOcppLogsOperatorId(
                      operatorId,
                      chargerData
                    );
                    navigate({
                      pathname: '/dashboard/OCPP-logs',
                      search: buildOcppLogsSearch({
                        chargerId: chargerData._id,
                        chargerName: chargerData.name ?? '',
                        operatorId: ocppOperatorId,
                      }),
                      state: {
                        charger: {
                          id: chargerData._id,
                          name: chargerData.name ?? '',
                        },
                        operatorId: ocppOperatorId,
                      },
                    });
                  }}
                />
              </Tooltip>
            )}
            <Tooltip title={t('buttons.refresh')}>
              <Button
                icon={<ReloadOutlined />}
                onClick={refetch}
              />
            </Tooltip>
          </div>
        </div>
      }
    >
      {isChargerError ? (
        <div className="text-center py-8">
          <p className="text-red-600">
            {chargerError?.response?.data?.message ||
              chargerError?.message ||
              t('chargerNotFound')}
          </p>
        </div>
      ) : (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          tabBarExtraContent={
            <>
              {activeTab == 2 && (
                <div className='flex justify-end mb-2'>
                  <Tooltip title={t('buttons.refresh')}>
                    <Button
                      loading={isOCPPLogsLoading}
                      icon={<ReloadOutlined />}
                      onClick={refechOcppLogs}
                    />
                  </Tooltip>
                </div>
              )}
              {
                activeTab == 1 && (
                  <div className='flex justify-end mb-2'>
                    <Tooltip title={t('AllTransactions')}>
                      <Button
                        icon={<BsListColumnsReverse />}
                        onClick={() => navigate(`/dashboard/charging-history`, {
                          state: {
                            charger: {
                              id: chargerData._id,
                              name: chargerData.name,
                              operator: chargerData.operatorId,
                            },
                          },
                        })}
                      />
                    </Tooltip>
                  </div>
                )
              }
            </>
          }
          items={[
            {
              key: '1',
              label: t('charger.details'),
              children: (
                <Space orientation="vertical" size="middle" className="w-full">
                  {chargerData?.ocppConnectionIssue &&
                    chargerData?.ocppConnectionIssue !== 'NO' && (
                      <Tag
                        variant="outlined"
                        color="red"
                        icon={<ExclamationCircleOutlined />}
                        className="!flex !items-center gap-1 !max-w-fit !ml-0"
                      >
                        {typeof chargerData.ocppConnectionIssue === 'string'
                          ? chargerData.ocppConnectionIssue
                          : t(
                            'charger.ocppConnectionIssueDetected',
                            'Connection Issue Detected'
                          )}
                      </Tag>
                    )}
                  <div className="flex gap-4 flex-col lg:flex-row w-full">
                    {images.length > 0 && (
                      <div className="flex flex-col gap-4 w-full lg:w-auto">
                        <Image.PreviewGroup>
                          <Image
                            alt="main-charger-image"
                            src={images[selectedImageIndex]}
                            height={isMobile ? 200 : 250}
                            width={isMobile ? '100%' : 250}
                            className="rounded-md w-full"
                            style={{ objectFit: 'cover' }}
                            preview={{ visible: false }}
                          />
                        </Image.PreviewGroup>
                        <div className="flex gap-2 flex-wrap justify-center lg:justify-start">
                          {images.map((img, idx) => (
                            <Image
                              alt={`charger-image-${img}`}
                              src={img}
                              key={img}
                              height={isMobile ? 50 : 60}
                              width={isMobile ? 50 : 60}
                              className="rounded-md cursor-pointer"
                              style={{
                                objectFit: 'cover',
                                opacity: selectedImageIndex === idx ? 0.7 : 1,
                              }}
                              preview={false}
                              onClick={() => setSelectedImageIndex(idx)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex-1 w-full lg:w-1/2">
                      <Descriptions
                        layout="horizontal"
                        column={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 }}
                        items={chargerDetailsItems}
                        bordered
                        title={t('charger.details')}
                        size="small"
                        labelStyle={{ fontWeight: 600 }}
                      />
                    </div>
                  </div>
                  <div className="text-sm font-medium mb-2">
                    {t('connector.details')}
                  </div>
                  <div className="overflow-x-auto">
                    <Table
                      dataSource={connectorsWithTransactions}
                      columns={connectorColumns}
                      rowKey="_id"
                      variant="outlined"
                      size={isMobile ? 'small' : 'small'}
                      pagination={false}
                      scroll={{ x: isMobile ? 600 : undefined }}
                    />
                  </div>
                  <Tabs
                    activeKey={nestedActiveTab}
                    onChange={setNestedActiveTab}
                    items={items}
                  />
                </Space>
              ),
            },
            {
              key: '2',
              label: t('OCPPLogs'),
              children: isError ? (
                <div className='text-red-500 text-center p-4'>{t('chargerOfflineOrNeverConfigured')}</div>
              ) : (
                <div className="overflow-x-auto">
                  <OCPPLogsTable
                    columns={columns}
                    dataSource={logs}
                    loading={isOCPPLogsLoading}
                    error={error}
                  />
                </div>
              ),
            },
          ]}
        />
      )}
      {/* Modals and Drawers (keep outside Tabs) */}
      <QRCodeModal
        isOpen={isQRModalOpen}
        onClose={() => setQRModalOpen(false)}
        chargerId={chargerData?._id}
        connectors={chargerData?.connectors || []}
        customerUrl={import.meta.env.VITE_CHARGER_CUSTOMER_URL}
      />
      <ConnectModal
        isOpen={isConnectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        chargerKey={chargerKey}
        serialNumber={serialNumber}
      />
      <TransactionDetailsModal
        open={isTransactionModalOpen}
        onClose={() => setTransactionModalOpen(false)}
        id={selectedTransactionId}
      />
      <Modal
        title={<Title level={4} className="mb-0!">{t('modal.confirmStopTitle')}</Title>}
        open={!!stopChargingModalOpen}
        confirmLoading={isStopChargingLoading}
        onOk={confirmStopCharging}
        onCancel={() => setStopChargingModalOpen(false)}
      >
        <p>{t('modal.confirmStopMessage')}</p>
      </Modal>
      <CreateCharger
        isOpen={openCharger}
        id={selectedChargerId}
        passOperatorId={operatorId}
        onOpenChange={() => {
          setOpenCharger(false);
          setSelectedChargerId(null);
        }}
        locationId={chargerData?.locationOwnerId}
      />
      {isOcppDrawerOpen && (
        <OcppCommandDrawer
          isOpen={isOcppDrawerOpen}
          onClose={() => setOcppDrawerOpen(false)}
          charger={chargerData}
          refetchLocation={refetch}
        />
      )}
      {selectedLocation && (
        <LocationSingleDrawer
          isOpen={!!selectedLocation}
          onClose={() => setSelectedLocation(null)}
          selectedLocation={selectedLocation}
          OPId={chargerData?.operatorId}
        />
      )}
      <Modal
        confirmLoading={isBlockLoading}
        title={
          <Title level={4} className="mb-0!">
            {blockAction === 'BLOCK'
              ? t('blockModalTitle')
              : t('unblockModalTitle')}
          </Title>
        }
        open={blockModalOpen}
        onCancel={() => setBlockModalOpen(false)}
        onOk={confirmBlockCharger}
      >
        <p>
          {blockAction === 'BLOCK'
            ? t('blockModalMessage')
            : t('unblockModalMessage')}
        </p>
      </Modal>
    </Drawer>
  );
};

// Add prop-types validation
SingleChargerDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedCharger: PropTypes.string,
  passoperatorId: PropTypes.string,
};

export default SingleChargerDrawer;
